import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function EmailSettings() {
  const [settings, setSettings] = useState({
    provider: "resend",
    api_key: "",
    from_email: "",
    from_name: "",
    // SMTP settings
    smtp_host: "mail.daliastile.com",
    smtp_port: 587,
    smtp_username: "info@daliastile.com",
    smtp_password: "",
    smtp_secure: false,
    templates: {
      order_confirmation: ""
    }
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('email_settings')
        .not('email_settings', 'is', null)
        .limit(1);

      if (error) throw error;
      
      if (data && data.length > 0) {
        const existingSettings = data[0].email_settings || {};
        setSettings(prev => ({
          ...prev,
          ...existingSettings
        }));
      }
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los ajustes de email",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { data: existingData, error: fetchError } = await supabase
        .from('app_settings')
        .select('id')
        .not('email_settings', 'is', null)
        .limit(1);

      if (fetchError) throw fetchError;

      if (existingData && existingData.length > 0) {
        const { error: updateError } = await supabase
          .from('app_settings')
          .update({
            email_settings: settings
          })
          .eq('id', existingData[0].id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('app_settings')
          .insert({
            email_settings: settings
          });

        if (insertError) throw insertError;
      }

      toast({
        title: "Éxito",
        description: "Configuración de email guardada correctamente"
      });
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast({
        title: "Error",
        description: "No se pudieron guardar los ajustes de email",
        variant: "destructive"
      });
    }
  };

  const handleProviderChange = (provider) => {
    setSettings(prev => ({
      ...prev,
      provider: provider
    }));
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Configuración de Email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Proveedor de Email */}
          <div className="space-y-2">
            <Label htmlFor="provider">Proveedor de Email</Label>
            <Select
              value={settings.provider}
              onValueChange={handleProviderChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="resend">Resend (Recomendado)</SelectItem>
                <SelectItem value="smtp">SMTP - Daliastile</SelectItem>
                <SelectItem value="sendgrid">SendGrid</SelectItem>
                <SelectItem value="mailgun">Mailgun</SelectItem>
                <SelectItem value="ses">Amazon SES</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Configuración común */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="from_email">Email Remitente</Label>
              <Input
                id="from_email"
                type="email"
                value={settings.from_email}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  from_email: e.target.value
                }))}
                placeholder="pedidos@tuempresa.com"
              />
            </div>

            <div>
              <Label htmlFor="from_name">Nombre Remitente</Label>
              <Input
                id="from_name"
                value={settings.from_name}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  from_name: e.target.value
                }))}
                placeholder="Tu Empresa"
              />
            </div>
          </div>

          {/* Configuración específica por proveedor */}
          {settings.provider === 'resend' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="api_key">API Key de Resend</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={settings.api_key}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    api_key: e.target.value
                  }))}
                  placeholder="Tu API Key de Resend"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Nota: Para usar dominios personalizados en Resend, primero debes verificar el dominio en el panel de Resend.
                </p>
              </div>
            </div>
          )}

          {settings.provider === 'smtp' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="smtp_host">Servidor SMTP</Label>
                  <Input
                    id="smtp_host"
                    value={settings.smtp_host}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      smtp_host: e.target.value
                    }))}
                    placeholder="mail.daliastile.com"
                  />
                </div>
                <div>
                  <Label htmlFor="smtp_port">Puerto</Label>
                  <Select
                    value={settings.smtp_port.toString()}
                    onValueChange={(value) => setSettings(prev => ({
                      ...prev,
                      smtp_port: parseInt(value),
                      smtp_secure: value === '465'
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25 (Sin TLS)</SelectItem>
                      <SelectItem value="587">587 (TLS)</SelectItem>
                      <SelectItem value="465">465 (SSL)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="smtp_username">Usuario SMTP</Label>
                <Input
                  id="smtp_username"
                  value={settings.smtp_username}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    smtp_username: e.target.value
                  }))}
                  placeholder="info@daliastile.com"
                />
              </div>

              <div>
                <Label htmlFor="smtp_password">Contraseña SMTP</Label>
                <Input
                  id="smtp_password"
                  type="password"
                  value={settings.smtp_password}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    smtp_password: e.target.value
                  }))}
                  placeholder="Tu contraseña SMTP"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="smtp_secure"
                  checked={settings.smtp_secure}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    smtp_secure: e.target.checked
                  }))}
                  className="rounded"
                />
                <Label htmlFor="smtp_secure">Usar SSL/TLS</Label>
              </div>
            </div>
          )}

          {settings.provider === 'sendgrid' && (
            <div>
              <Label htmlFor="api_key">API Key de SendGrid</Label>
              <Input
                id="api_key"
                type="password"
                value={settings.api_key}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  api_key: e.target.value
                }))}
                placeholder="Tu API Key de SendGrid"
              />
            </div>
          )}

          {settings.provider === 'mailgun' && (
            <div>
              <Label htmlFor="api_key">API Key de Mailgun</Label>
              <Input
                id="api_key"
                type="password"
                value={settings.api_key}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  api_key: e.target.value
                }))}
                placeholder="Tu API Key de Mailgun"
              />
            </div>
          )}

          {settings.provider === 'ses' && (
            <div>
              <Label htmlFor="api_key">API Key de Amazon SES</Label>
              <Input
                id="api_key"
                type="password"
                value={settings.api_key}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  api_key: e.target.value
                }))}
                placeholder="Tu API Key de Amazon SES"
              />
            </div>
          )}

          {/* Template ID (opcional) */}
          <div>
            <Label htmlFor="template_id">Template ID (Opcional)</Label>
            <Input
              id="template_id"
              value={settings.templates?.order_confirmation || ""}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                templates: {
                  ...prev.templates,
                  order_confirmation: e.target.value
                }
              }))}
              placeholder="ID del template de confirmación"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit">
          Guardar Cambios
        </Button>
      </div>
    </form>
  );
} 